import type { FunctionComponent } from 'react';
import { useState } from 'react';
import {
  Briefcase,
  Building2,
  Calendar,
  ChevronDown,
  FileText,
  Globe,
  Hash,
  MapPin,
  User,
  Users,
} from 'lucide-react';

import type {
  ExtractRegistryInformationAdapterRawByVendor,
  RegistryInformationVendor,
} from './utils';
import { ctw } from '@/common';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/atoms/Collapsible';
import { hasValue } from './utils';

export const KyckrRenderRaw: FunctionComponent<{
  raw: ExtractRegistryInformationAdapterRawByVendor<typeof RegistryInformationVendor.KYCKR>;
}> = ({ raw }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Filter individuals that have additional info beyond just name (name is already in processed)
  const representativeIndividuals =
    raw.representatives?.individuals?.filter(ind =>
      [
        ind.birthdate?.normalized,
        ind.nationality,
        ind.idNumber,
        ind.placeOfResidence?.fullAddress,
      ].some(hasValue),
    ) ?? [];

  const representativeCorporations =
    raw.representatives?.corporations?.filter(
      corp => hasValue(corp.registrationNumber) || hasValue(corp.registrationAuthority),
    ) ?? [];

  const uboIndividuals =
    raw.ultimateBeneficialOwners?.individuals?.filter(ind =>
      [ind.birthdate?.normalized, ind.nationality, ind.natureOfControl].some(hasValue),
    ) ?? [];

  const uboCorporations =
    raw.ultimateBeneficialOwners?.corporations?.filter(corp =>
      [corp.registrationNumber, corp.natureOfControl].some(hasValue),
    ) ?? [];

  const addresses =
    raw.addresses?.filter(addr =>
      [
        addr.type,
        addr.fullAddress,
        addr.streetNumber,
        addr.streetName,
        addr.city,
        addr.region,
        addr.postcode,
        addr.country,
      ].some(hasValue),
    ) ?? [];

  const activities =
    raw.activities?.filter(activity =>
      [activity.code, activity.description, activity.type, activity.classificationScheme].some(
        hasValue,
      ),
    ) ?? [];

  const activityDeclarations =
    raw.activityDeclarations?.filter(decl =>
      [decl.declaration, decl.declarationDescription, decl.language].some(hasValue),
    ) ?? [];

  const capitalItems =
    raw.capital?.filter(cap =>
      [cap.classDescription, cap.quantity, cap.totalNominalValue, cap.votingRights].some(hasValue),
    ) ?? [];

  const hasRepresentatives =
    representativeIndividuals.length > 0 || representativeCorporations.length > 0;
  const hasUBOs = uboIndividuals.length > 0 || uboCorporations.length > 0;
  const hasAddresses = addresses.length > 0;
  const hasActivities = activities.length > 0;
  const hasActivityDeclarations = activityDeclarations.length > 0;
  const hasContactDetails = raw.contactDetails && Object.values(raw.contactDetails).some(hasValue);
  const sourceLinks = raw.sourceLinks?.filter(link => hasValue(link.url)) ?? [];
  const hasLinks = (raw.links && Object.values(raw.links).some(hasValue)) || sourceLinks.length > 0;
  const hasCapital = capitalItems.length > 0;
  const hasTotalCapital =
    raw.totalCapital && [raw.totalCapital.amount, raw.totalCapital.currency].some(hasValue);
  const hasLastAnnualAccountDate =
    raw.lastAnnualAccountDate &&
    [raw.lastAnnualAccountDate.normalized, raw.lastAnnualAccountDate.original].some(hasValue);
  const hasRegisteredAgentAddress = hasValue(raw.registeredAgentAddress);

  const hasAnyContent = [
    hasRepresentatives,
    hasUBOs,
    hasAddresses,
    hasActivities,
    hasActivityDeclarations,
    hasContactDetails,
    hasLinks,
    hasCapital,
    hasTotalCapital,
    hasLastAnnualAccountDate,
    hasRegisteredAgentAddress,
  ].some(Boolean);

  if (!hasAnyContent) {
    return null;
  }

  return (
    <div className="mt-8">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger className="flex items-center gap-1 text-sm text-blue-600 transition-colors hover:text-blue-800 hover:underline">
          {isOpen ? 'Show less' : 'Show additional information'}
          <ChevronDown className={ctw('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
        </CollapsibleTrigger>

        <CollapsibleContent
          className={ctw(
            'space-y-6',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2',
          )}
        >
          {hasRepresentatives && (
            <div className="border-t pt-6">
              <h3 className="mb-4 flex items-center gap-2 text-base font-semibold">
                <User size={18} />
                Representatives
              </h3>
              <div className="space-y-3">
                {representativeIndividuals.map((ind, idx) => (
                  <div key={idx} className="rounded-lg border bg-white p-4">
                    <div className="mb-2 text-sm font-semibold">{ind.name}</div>
                    {ind.birthdate?.normalized && (
                      <div className="mb-1 text-xs text-slate-600">{ind.birthdate.normalized}</div>
                    )}
                    {ind.nationality && (
                      <div className="mb-1 text-xs text-slate-600">{ind.nationality}</div>
                    )}
                    {ind.idNumber && (
                      <div className="mb-1 text-xs text-slate-600">ID: {ind.idNumber}</div>
                    )}
                    {ind.placeOfResidence?.fullAddress && (
                      <div className="text-xs text-slate-600">
                        {ind.placeOfResidence.fullAddress}
                      </div>
                    )}
                  </div>
                ))}
                {representativeCorporations.map((corp, idx) => (
                  <div key={idx} className="rounded-lg border bg-white p-4">
                    <div className="mb-2 text-sm font-semibold">{corp.name}</div>
                    {corp.registrationNumber && (
                      <div className="mb-1 text-xs text-slate-600">
                        Reg: {corp.registrationNumber}
                      </div>
                    )}
                    {corp.registrationAuthority && (
                      <div className="text-xs text-slate-600">{corp.registrationAuthority}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasUBOs && (
            <div className="border-t pt-6">
              <h3 className="mb-4 flex items-center gap-2 text-base font-semibold">
                <Users size={18} />
                Ultimate Beneficial Owners
              </h3>
              <div className="space-y-3">
                {uboIndividuals.map((ind, idx) => (
                  <div key={idx} className="rounded-lg border bg-white p-4">
                    <div className="mb-2 text-sm font-semibold">{ind.name}</div>
                    {ind.birthdate?.normalized && (
                      <div className="mb-1 text-xs text-slate-600">{ind.birthdate.normalized}</div>
                    )}
                    {ind.nationality && (
                      <div className="mb-1 text-xs text-slate-600">{ind.nationality}</div>
                    )}
                    {ind.natureOfControl?.length && (
                      <div className="mt-2 text-xs text-slate-600">
                        Control: {ind.natureOfControl.join(', ')}
                      </div>
                    )}
                  </div>
                ))}
                {uboCorporations.map((corp, idx) => (
                  <div key={idx} className="rounded-lg border bg-white p-4">
                    <div className="mb-2 text-sm font-semibold">{corp.name}</div>
                    {corp.registrationNumber && (
                      <div className="mb-1 text-xs text-slate-600">
                        Reg: {corp.registrationNumber}
                      </div>
                    )}
                    {corp.natureOfControl?.length && (
                      <div className="mt-2 text-xs text-slate-600">
                        Control: {corp.natureOfControl.join(', ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasAddresses && (
            <div className="border-t pt-6">
              <h3 className="mb-4 flex items-center gap-2 text-base font-semibold">
                <MapPin size={18} />
                Addresses
              </h3>
              <div className="space-y-3">
                {addresses.map((addr, idx) => {
                  const composedAddress = [
                    [addr.streetNumber, addr.streetName].filter(Boolean).join(' '),
                    addr.city,
                    addr.region,
                    addr.postcode,
                    addr.country,
                  ]
                    .filter(Boolean)
                    .join(', ');

                  const displayAddress = composedAddress || addr.fullAddress;

                  return (
                    <div key={idx} className="rounded-lg border bg-white p-4">
                      {addr.type && <div className="mb-2 text-xs text-slate-500">{addr.type}</div>}
                      {displayAddress && (
                        <div className="text-sm text-slate-700">{displayAddress}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {hasActivities && (
            <div className="border-t pt-6">
              <h3 className="mb-4 flex items-center gap-2 text-base font-semibold">
                <Briefcase size={18} />
                Activities
              </h3>
              <div className="space-y-2">
                {activities.map((activity, idx) => (
                  <div key={idx} className="rounded-lg border bg-white p-3">
                    <div className="text-sm font-semibold text-slate-900">{activity.code}</div>
                    {activity.description && (
                      <div className="mt-1 text-xs text-slate-600">{activity.description}</div>
                    )}
                    {(activity.type || activity.classificationScheme) && (
                      <div className="mt-2 text-xs text-slate-500">
                        {activity.type && <span>{activity.type}</span>}
                        {activity.type && activity.classificationScheme && <span> • </span>}
                        {activity.classificationScheme && (
                          <span>{activity.classificationScheme}</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasActivityDeclarations && (
            <div className="border-t pt-6">
              <h3 className="mb-4 flex items-center gap-2 text-base font-semibold">
                <FileText size={18} />
                Activity Declarations
              </h3>
              <div className="space-y-2">
                {activityDeclarations.map((decl, idx) => (
                  <div key={idx} className="rounded-lg border bg-white p-3">
                    <div className="text-sm text-slate-700">{decl.declaration}</div>
                    {decl.declarationDescription && (
                      <div className="mt-1 text-xs text-slate-600">
                        {decl.declarationDescription}
                      </div>
                    )}
                    {decl.language && (
                      <div className="mt-2 text-xs text-slate-500">{decl.language}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasContactDetails && (
            <div className="border-t pt-6">
              <h3 className="mb-4 flex items-center gap-2 text-base font-semibold">
                <Globe size={18} />
                Contact Details
              </h3>
              <div className="space-y-2 rounded-lg border bg-white p-4">
                {raw.contactDetails?.email && (
                  <div className="text-sm">
                    <span className="text-slate-600">Email: </span>
                    <span className="text-slate-900">{raw.contactDetails.email}</span>
                  </div>
                )}
                {raw.contactDetails?.telNumber && (
                  <div className="text-sm">
                    <span className="text-slate-600">Phone: </span>
                    <span className="text-slate-900">{raw.contactDetails.telNumber}</span>
                  </div>
                )}
                {raw.contactDetails?.fax && (
                  <div className="text-sm">
                    <span className="text-slate-600">Fax: </span>
                    <span className="text-slate-900">{raw.contactDetails.fax}</span>
                  </div>
                )}
                {raw.contactDetails?.website && (
                  <div className="text-sm">
                    <span className="text-slate-600">Website: </span>
                    <span className="text-slate-900">{raw.contactDetails.website}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {hasCapital && (
            <div className="border-t pt-6">
              <h3 className="mb-4 flex items-center gap-2 text-base font-semibold">
                <Building2 size={18} />
                Capital Classes
              </h3>
              <div className="space-y-3">
                {capitalItems.map((cap, idx) => (
                  <div key={idx} className="rounded-lg border bg-white p-4">
                    {cap.classDescription && (
                      <div className="mb-2 text-sm font-semibold">{cap.classDescription}</div>
                    )}
                    <div className="space-y-1 text-xs text-slate-600">
                      {cap.quantity && <div>Quantity: {cap.quantity}</div>}
                      {cap.totalNominalValue && (
                        <div>
                          Value: {cap.totalNominalValue} {cap.currency}
                        </div>
                      )}
                      {cap.votingRights && <div>Voting Rights: {cap.votingRights}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasTotalCapital && (
            <div className="border-t pt-6">
              <h3 className="mb-4 flex items-center gap-2 text-base font-semibold">
                <Hash size={18} />
                Total Capital
              </h3>
              <div className="rounded-lg border bg-white p-4">
                <div className="mb-2 text-sm font-semibold text-slate-900">
                  {raw.totalCapital?.amount} {raw.totalCapital?.currency}
                </div>
                {raw.totalCapital?.type && (
                  <div className="text-xs text-slate-600">{raw.totalCapital.type}</div>
                )}
              </div>
            </div>
          )}

          {hasLastAnnualAccountDate && (
            <div className="border-t pt-6">
              <h3 className="mb-4 flex items-center gap-2 text-base font-semibold">
                <Calendar size={18} />
                Last Annual Account
              </h3>
              <div className="rounded-lg border bg-white p-4">
                <div className="text-sm text-slate-900">
                  {raw.lastAnnualAccountDate?.normalized || raw.lastAnnualAccountDate?.original}
                </div>
              </div>
            </div>
          )}

          {hasLinks && (
            <div className="border-t pt-6">
              <h3 className="mb-4 flex items-center gap-2 text-base font-semibold">
                <Globe size={18} />
                Reference Links
              </h3>
              <div className="space-y-2 rounded-lg border bg-white p-4">
                {raw.links?.document && (
                  <div className="text-sm">
                    <a
                      href={raw.links.document}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      Document
                    </a>
                  </div>
                )}
                {raw.links?.data && (
                  <div className="text-sm">
                    <a
                      href={raw.links.data}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      Data Source
                    </a>
                  </div>
                )}
                {sourceLinks.map((link, index) => (
                  <div key={index} className="text-sm">
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {link.label ?? link.url}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasRegisteredAgentAddress && (
            <div className="border-t pt-6">
              <h3 className="mb-4 flex items-center gap-2 text-base font-semibold">
                <MapPin size={18} />
                Registered Agent Address
              </h3>
              <div className="rounded-lg border bg-white p-4">
                <div className="text-sm text-slate-700">{raw.registeredAgentAddress}</div>
              </div>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
